'use client';

import React, { useCallback, useState } from 'react';
import { useCurve } from '@/context/CurveContext';
import { MaturityKey } from '../data/initialData';
import DataPageHeader from './DataPageHeader';
import YieldCurveChart from './YieldCurveChart';
import MaturitySelector from './MaturitySelector';
import SimulatorStatCards from './SimulatorStatCards';
import CreditbookPanel from './CreditbookPanel';
import MaturityVisibilityToggle from './MaturityVisibilityToggle';

export default function DataPageClient() {
  const { maturities, selectedMaturity, setSelectedMaturity, hiddenMaturities, toggleMaturityVisibility } = useCurve();
  const [flashKey, setFlashKey] = useState(0);

  const selectedData = maturities.find((m) => m.maturity === selectedMaturity)!;

  // Filtered maturities for the chart only — underlying data is untouched
  const visibleMaturities = maturities.filter((m) => !hiddenMaturities.has(m.maturity));

  const handleMaturitySelect = useCallback((maturity: MaturityKey) => {
    setSelectedMaturity(maturity);
    setFlashKey((k) => k + 1);
  }, [setSelectedMaturity]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8F7FC] to-[#F3F1FA]">
      <DataPageHeader />

      <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 py-6 space-y-6">

        {/* Page title block */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 pt-2 pb-1">
          <div>
            <p className="section-label mb-1.5">Fixed-Rate Credit Market</p>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Bitcoin-Backed Yield Curve

            </h1>
            <p className="text-sm text-gray-500 mt-1">Fixed-Rate Credit Market Simulator</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
              <span className="w-2 h-2 rounded-full bg-[#6C47FF] inline-block" />
              <span className="text-xs font-semibold text-gray-600">6 Active Maturities</span>
            </div>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
              <span className="text-xs font-semibold text-gray-500">Total Supply</span>
              <span className="text-xs font-mono font-bold text-[#6C47FF]">
                ${(maturities.reduce((s, m) => s + m.totalLiquidity, 0) / 1_000_000).toFixed(1)}M
              </span>
            </div>
          </div>
        </div>

        {/* Main chart card */}
        <div className="spine-card p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="section-label mb-1">Yield Curve</p>
              <h2 className="text-base font-semibold text-gray-900">
                BTC-Backed Loans — Fixed Rate APR with Supply Depth Overlay
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-3 py-1">
                <span className="w-2 h-2 rounded-full bg-[#6C47FF] inline-block" />
                Click a maturity to explore depth
              </span>
            </div>
          </div>
          <YieldCurveChart
            maturities={visibleMaturities}
            selectedMaturity={selectedMaturity}
            onSelectMaturity={handleMaturitySelect} />

          {/* Maturity visibility toggles — visual only, no data changes */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <MaturityVisibilityToggle
              maturities={maturities}
              hiddenMaturities={hiddenMaturities}
              onToggle={toggleMaturityVisibility} />

          </div>
        </div>

        {/* Maturity Pills */}
        <MaturitySelector
          maturities={maturities}
          selectedMaturity={selectedMaturity}
          onSelect={handleMaturitySelect} />


        {/* Stats Row */}
        <SimulatorStatCards key={flashKey} data={selectedData} />

        {/* Creditbook Depth Panel */}
        <CreditbookPanel key={`cb-${flashKey}`} data={selectedData} />

        {/* Footer */}
        <div className="flex items-center justify-center pb-6">
          <p className="text-xs text-gray-400 text-center max-w-lg">
            All values are manually configured for demonstration purposes. This simulator does not reflect live market data.
            <br />
            <span className="text-[#6C47FF] font-medium">Spine Finance</span> — Bitcoin-Backed Fixed-Rate Credit Infrastructure
          </p>
        </div>
      </main>
    </div>);

}